/**
 * LOCAL PRODUCTION-CANDIDATE DRAFT. Not an authoritative checkout safeguard.
 * Deploy with champion-artwork-core.js, unchanged from core/artwork-cart.mjs.
 * Browser interception can be bypassed. A server-side checkout validator is still
 * required before treating this policy as enforceable. See adapter/README.md.
 */
import { artworkProperties, groupForSubmission } from './champion-artwork-core.js';
import { planVariantArtworkReconciliation } from './champion-artwork-variants.js';

export const SCOPE = Object.freeze({
  eligibilityTag: 'artwork-fee-enabled',
  serviceProductId: '9616616882348',
  serviceVariantId: '47616038994092',
});
const KEYS = ['_ct_artwork_group', '_ct_artwork_choice', '_ct_artwork_role'];
const QUOTE_KEYS = ['_ct_artwork_fee_cents', '_ct_artwork_fee_currency'];
const OPTION_PROPERTY = 'Artwork Options '; // Zepto's trailing space is significant.
const SELECTOR = '#pplr_html select[name="properties[Artwork Options ]"]';
const ADD_SELECTOR = '#pplr_html .p_a_t_c[name="add"], .p_a_t_c[name="add"]';
const CHOICES = Object.freeze({
  'Upload artwork for review': 'review',
  'Help me create my design': 'create_fix',
});
const own = (o, key) => Object.prototype.hasOwnProperty.call(o, key);
const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const validId = (value) => typeof value === 'string' && /^[1-9]\d*$/.test(value);

/** Trusted Liquid records only. Matching duplicate records union their variants. */
export function normalizeKnownProducts(records) {
  if (!Array.isArray(records)) return null;
  const products = new Map();
  const variants = new Map();
  for (const record of records) {
    if (!object(record) || !validId(record.productId) || typeof record.eligible !== 'boolean' ||
      !Array.isArray(record.variantIds) || !record.variantIds.length || record.variantIds.some((id) => !validId(id))) return null;
    const artworkVariantId = record.artworkVariantId ?? SCOPE.serviceVariantId;
    if (!validId(artworkVariantId)) return null;
    let product = products.get(record.productId);
    if (product && product.artworkVariantId !== artworkVariantId) return null;
    if (product && product.eligible !== record.eligible) return null;
    if (!product) {
      product = { productId: record.productId, eligible: record.eligible, artworkVariantId, variantIds: [] };
      products.set(record.productId, product);
    }
    for (const id of record.variantIds) {
      if (variants.has(id) && variants.get(id) !== record.productId) return null;
      variants.set(id, record.productId);
      if (!product.variantIds.includes(id)) product.variantIds.push(id);
    }
  }
  return [...products.values()];
}

export function choiceFromLabel(label) {
  return typeof label === 'string' && own(CHOICES, label) ? CHOICES[label] : null;
}

/** Exact, deliberately opt-in activation. Does not patch any transport if false. */
export function validConfiguration(config, themeId) {
  const known = normalizeKnownProducts(config?.knownProducts);
  return object(config) && config.enabled === true &&
    typeof config.initialCartHasArtwork === 'boolean' &&
    typeof config.currency === 'string' && /^[A-Z]{3}$/.test(config.currency) &&
    Object.entries(SCOPE).every(([key, value]) => config[key] === value) &&
    known !== null && Array.isArray(config.serviceVariantIds) && config.serviceVariantIds.length > 0 &&
    config.serviceVariantIds.every(validId) && config.serviceVariantIds.includes(SCOPE.serviceVariantId) &&
    (config.currentProductId === null ||
      (validId(config.currentProductId) && known.some((record) => record.productId === config.currentProductId))) &&
    typeof config.themeId === 'string' && /^[1-9]\d*$/.test(config.themeId) &&
    config.themeId === String(themeId) &&
    typeof config.serviceProductHandle === 'string' &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(config.serviceProductHandle);
}

/**
 * Pure transport rewrite. Only target-variant rows receive three reserved keys.
 * Other fields, upload File/Blob objects, quantities and unrelated rows survive.
 * metadataForItem receives the original properties; it must return reserved keys.
 */
export function transformCartAddBody(body, { contentType = '', metadataForItem, isEligibleVariant = () => false,
  FormDataClass = globalThis.FormData, URLSearchParamsClass = globalThis.URLSearchParams } = {}) {
  const groups = [];
  const metadata = (properties, variantId) => {
    const result = metadataForItem(properties, String(variantId));
    if (result === null) return null; // A tag alone is not an artwork choice.
    if (!object(result) || KEYS.some((key) => typeof result[key] !== 'string')) {
      throw new Error('Artwork submission metadata could not be verified.');
    }
    groups.push(result._ct_artwork_group);
    return Object.fromEntries([...KEYS, ...QUOTE_KEYS].filter((key) => own(result, key)).map((key) => [key, result[key]]));
  };
  const unchanged = () => ({ body, touched: false, groups: [] });

  let json;
  let jsonString = false;
  if (typeof body === 'string' && (/\bjson\b/i.test(contentType) || /^\s*\{/.test(body))) {
    try { json = JSON.parse(body); jsonString = true; } catch { return unchanged(); }
  } else if (object(body) && Object.getPrototypeOf(body) === Object.prototype) {
    // Useful to callers/tests; native fetch does not itself accept object bodies.
    json = body;
  }
  if (json !== undefined) {
    if (!object(json)) return unchanged();
    const rewrite = (item) => {
      if (!object(item) || !isEligibleVariant(item.id)) return item;
      if (item.properties != null && !object(item.properties)) {
        throw new Error('Unsupported artwork properties. Please retry using the product form.');
      }
      const properties = item.properties || {};
      const reserved = metadata(properties, item.id);
      return reserved ? { ...item, properties: { ...properties, ...reserved } } : item;
    };
    const next = Array.isArray(json.items) ? { ...json, items: json.items.map(rewrite) } : rewrite(json);
    return groups.length ? { body: jsonString ? JSON.stringify(next) : next, touched: true, groups: [...new Set(groups)] } : unchanged();
  }

  const form = FormDataClass && body instanceof FormDataClass;
  const params = URLSearchParamsClass && body instanceof URLSearchParamsClass;
  const encoded = typeof body === 'string' &&
    (/application\/x-www-form-urlencoded/i.test(contentType) || /(?:^|&)\s*(?:id|items(?:%5B|\[))/.test(body));
  if (!form && !params && !encoded) return unchanged();
  const source = encoded ? new URLSearchParamsClass(body) : body;
  let entries = [...source.entries()];
  if (!entries.some(([key, value]) => /^(?:id|items\[(?:\d*)\]\[id\])$/.test(key) && isEligibleVariant(value))) return unchanged();

  // Unindexed items[][id] is supported only as contiguous, id-first item blocks.
  // Convert those blocks to explicit indexes to avoid multipart parser ambiguity.
  if (entries.some(([key]) => key.startsWith('items[]['))) {
    if (entries.some(([key]) => /^items\[\d+\]/.test(key))) {
      throw new Error('Mixed indexed and unindexed artwork items are not supported.');
    }
    let index = -1;
    entries = entries.map(([key, value]) => {
      if (!key.startsWith('items[][')) return [key, value];
      if (key === 'items[][id]') index += 1;
      if (index < 0) throw new Error('Artwork item fields must begin with the variant ID.');
      return [key.replace('items[]', `items[${index}]`), value];
    });
  }
  const prefixes = new Map();
  for (const [key, value] of entries) {
    if (key === 'id' && isEligibleVariant(value)) prefixes.set('', { propertyPrefix: 'properties[', variantId: value });
    const match = /^items\[(\d+)\]\[id\]$/.exec(key);
    if (match && isEligibleVariant(value)) prefixes.set(`items[${match[1]}]`, { propertyPrefix: `items[${match[1]}][properties][`, variantId: value });
  }
  const replacements = new Map();
  for (const [prefix, { propertyPrefix, variantId }] of prefixes) {
    const properties = {};
    for (const [key, value] of entries) {
      if (key.startsWith(propertyPrefix) && key.endsWith(']')) {
        const property = key.slice(propertyPrefix.length, -1);
        if (property === OPTION_PROPERTY && own(properties, property) && properties[property] !== value) {
          throw new Error('Conflicting artwork selections in the same submission.');
        }
        properties[property] = value;
      }
    }
    const reserved = metadata(properties, variantId);
    if (!reserved) continue;
    for (const [key, value] of Object.entries(reserved)) {
      replacements.set(prefix ? `${prefix}[properties][${key}]` : `properties[${key}]`, value);
    }
  }
  if (!groups.length) return unchanged();
  const output = form ? new FormDataClass() : new URLSearchParamsClass();
  for (const [key, value] of entries) if (!replacements.has(key)) output.append(key, value);
  for (const [key, value] of replacements) output.append(key, value);
  return { body: encoded ? output.toString() : output, touched: true, groups: [...new Set(groups)] };
}

export function installArtworkService(win, doc, config) {
  if (win.ChampionArtworkService) return win.ChampionArtworkService;
  if (!validConfiguration(config, win.Shopify?.theme?.id)) {
    return (win.ChampionArtworkService = Object.freeze({
      disabled: true, whenIdle: async () => true, reconcile: async () => true,
    }));
  }
  const originalFetch = win.fetch.bind(win);
  const HeadersClass = win.Headers;
  const RequestClass = win.Request;
  const XHR = win.XMLHttpRequest;
  const originalOpen = XHR?.prototype.open;
  const originalSend = XHR?.prototype.send;
  const originalSetHeader = XHR?.prototype.setRequestHeader;
  const originalAbort = XHR?.prototype.abort;
  const xhrState = new WeakMap();
  const root = new URL(win.Shopify?.routes?.root || '/', win.location.origin);
  if (root.origin !== win.location.origin) throw new Error('Unexpected Shopify route origin.');
  const cartURL = (suffix) => new URL(`cart${suffix}`, root).href;
  const knownProducts = normalizeKnownProducts(config.knownProducts);
  const productsById = new Map(knownProducts.map((product) => [product.productId, product]));
  const productsByVariant = new Map(knownProducts.flatMap((product) => product.variantIds.map((id) => [id, product])));
  const currentProduct = productsById.get(config.currentProductId);
  const serviceProductId = SCOPE.serviceProductId;
  const serviceIds = new Set(config.serviceVariantIds);
  const currentServiceId = currentProduct?.artworkVariantId ?? SCOPE.serviceVariantId;
  const feeIdForGroup = (group, items) => items?.[0] ? productsByVariant.get(String(items[0].variant_id))?.artworkVariantId : currentServiceId;
  const quoteForGroup = (group, items, quotes) => quotes[feeIdForGroup(group, items)];
  const quoteForAction = (action, groups, quotes) => quotes[action?.id ?? feeIdForGroup(action?.groupId, groups.get(action?.groupId))];
  const currentProductEnabled = currentProduct?.eligible === true && currentProduct.productId !== serviceProductId;
  const plannerConfig = { allowStaleFeeRepricing: true, serviceProductId, serviceVariantId: SCOPE.serviceVariantId, serviceVariantIds: config.serviceVariantIds, knownProducts, currency: config.currency };
  let submission = null;
  let externalPending = 0;
  let epoch = 0;
  let dirty = false;
  let running = null;
  let ready = false;
  let relevant = config.initialCartHasArtwork;
  let lastCart = null;
  let transportBlocker = null;
  let unconfirmedMutation = false;
  let idleWaiters = [];
  let notice = null;
  let mutationObserver;
  let displayedQuote = null;
  let displayRequest = null;
  let priceMessage = null;
  let priceApprovalPending = false;
  const quoteKey = `champion-artwork-service:accepted-prices:${SCOPE.serviceVariantId}`;
  let acceptedQuotes = {};
  let quoteStoreError = null;
  try {
    acceptedQuotes = JSON.parse(win.sessionStorage.getItem(quoteKey) || '{}');
    if (!object(acceptedQuotes) || Object.values(acceptedQuotes).some((quote) => !validQuote(quote))) throw new Error('Invalid artwork price acknowledgment.');
  } catch { quoteStoreError = 'The accepted artwork price could not be verified.'; }
  let reloadScheduled = false;
  let reloadIssued = false;
  let firstVerifiedPass = true;
  let eligibilityReloadScheduled = false;
  const eligibilityReloadKey = `champion-artwork-service:eligibility-refresh:${config.themeId}`;
  const reloadKey = `champion-artwork-service:cart-refresh:${config.themeId}`;
  const journalKey = `champion-artwork-service:pending:${SCOPE.serviceVariantId}`;
  const legacyJournalKeys = new Set();
  let pendingFeeAdds = [];
  let journalError = null;
  try {
    const keys = new Set([journalKey, `champion-artwork-service:pending:${config.themeId}`]);
    // Only our exact numeric-suffix prefix is read; never inspect other storage.
    for (let i = 0; i < (win.sessionStorage.length || 0); i += 1) {
      const key = win.sessionStorage.key(i);
      if (/^champion-artwork-service:pending:[1-9]\d*$/.test(key || '')) keys.add(key);
    }
    for (const key of keys) {
      const saved = win.sessionStorage.getItem(key);
      if (saved === null) continue;
      const records = JSON.parse(saved);
      if (!Array.isArray(records) || records.some((entry) =>
        !object(entry) || typeof entry.groupId !== 'string' || !/^ctaw_[A-Za-z0-9_.:-]+$/.test(entry.groupId) ||
        !(entry.cartId === null || typeof entry.cartId === 'string'))) throw new Error('Invalid pending artwork transaction.');
      if (key !== journalKey) legacyJournalKeys.add(key);
      pendingFeeAdds.push(...records.map(({ cartId, groupId }) => ({ cartId, groupId })));
    }
    pendingFeeAdds = [...new Map(pendingFeeAdds.map((record) => [JSON.stringify([record.cartId, record.groupId]), record])).values()];
    if (legacyJournalKeys.size) {
      const serialized = JSON.stringify(pendingFeeAdds);
      win.sessionStorage.setItem(journalKey, serialized);
      // Keep synchronized legacy records so rolling back cannot lose quarantine.
      for (const key of legacyJournalKeys) win.sessionStorage.setItem(key, serialized);
    }
  } catch { journalError = 'This browser could not read the pending artwork charge record.'; }
  const repriceJournalKey = `champion-artwork-service:pending-reprices:${SCOPE.serviceVariantId}`;
  let pendingReprices = [];
  let repriceJournalError = null;
  try {
    pendingReprices = JSON.parse(win.sessionStorage.getItem(repriceJournalKey) || '[]');
    if (!Array.isArray(pendingReprices) || pendingReprices.some((record) => !object(record) ||
      typeof record.groupId !== 'string' || !/^ctaw_[A-Za-z0-9_.:-]+$/.test(record.groupId) ||
      typeof record.key !== 'string' || !record.key || !(record.cartId === null || typeof record.cartId === 'string'))) {
      throw new Error('Invalid pending artwork repricing record.');
    }
  } catch { pendingReprices = []; repriceJournalError = 'This browser could not verify an earlier artwork price update.'; }
  function writePendingReprices(records) {
    if (repriceJournalError) throw new Error(repriceJournalError);
    try { win.sessionStorage.setItem(repriceJournalKey, JSON.stringify(records)); pendingReprices = records; }
    catch { repriceJournalError = 'This browser could not safely remember an artwork price update.'; throw new Error(repriceJournalError); }
  }
  const disabledByUs = new Map();
  const checkoutSelector = 'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], .shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, [data-shopify-buttoncontainer]';

  function announce(message, error = false) {
    if (!notice && doc.body) {
      notice = doc.createElement('div');
      notice.id = 'champion-artwork-service-notice';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.style.cssText = 'position:fixed;left:16px;right:16px;bottom:16px;z-index:2147483000;padding:12px 16px;background:#fff6df;color:#222;border:1px solid #9b7425;border-radius:5px;font:15px/1.45 sans-serif;box-shadow:0 2px 12px #0002';
      doc.body.appendChild(notice);
    }
    if (!notice) return;
    notice.textContent = message || '';
    notice.hidden = !message;
    notice.setAttribute('data-error', error ? 'true' : 'false');
  }
  function updateCheckout() {
    const mustBlock = relevant && !ready;
    for (const node of doc.querySelectorAll(checkoutSelector)) {
      if (mustBlock) {
        if (!disabledByUs.has(node)) {
          disabledByUs.set(node, { aria: node.getAttribute('aria-disabled') });
        }
        if (node.getAttribute('aria-disabled') !== 'true') node.setAttribute('aria-disabled', 'true');
      }
    }
    if (!mustBlock) {
      for (const [node, prior] of disabledByUs) {
        if (prior.aria === null) node.removeAttribute('aria-disabled');
        else node.setAttribute('aria-disabled', prior.aria);
      }
      disabledByUs.clear();
    }
  }
  function block(message, codes = []) {
    ready = false;
    announce(relevant ? `${message} Checkout is paused until artwork charges are verified.` : message, true);
    updateCheckout();
    doc.dispatchEvent(new win.CustomEvent('champion:artwork-service-blocked', { detail: { codes } }));
    return false;
  }
  function routeOf(input, method = 'GET') {
    let url;
    try { url = new URL(typeof input === 'string' || input instanceof win.URL ? input : input.url, win.location.href); }
    catch { return null; }
    if (url.origin !== win.location.origin || /^(?:GET|HEAD)$/i.test(method)) return null;
    for (const action of ['add', 'change', 'update', 'clear']) {
      if (url.pathname === new URL(cartURL(`/${action}`)).pathname || url.pathname === new URL(cartURL(`/${action}.js`)).pathname) return action;
    }
    return null;
  }
  function newSubmission() {
    if (typeof win.crypto?.randomUUID !== 'function') throw new Error('This browser cannot create a safe artwork submission ID.');
    return { groupId: groupForSubmission(win.crypto.randomUUID()), completed: false, pending: 0, choice: null };
  }
  function priceError(message) { const error = new Error(message); error.code = 'ARTWORK_PRICE_NOT_ACCEPTED'; return error; }
  function getMetadata(properties, variantId) {
    const owner = productsByVariant.get(variantId);
    const label = own(properties, OPTION_PROPERTY) ? properties[OPTION_PROPERTY] :
      owner?.productId === config.currentProductId ? doc.querySelector(SELECTOR)?.value : null;
    const choice = choiceFromLabel(label);
    if (!choice) {
      if (KEYS.some((key) => own(properties, key))) throw new Error('The artwork choice could not be verified. Please return to the product form.');
      return null;
    }
    relevant = true;
    if (!submission) submission = newSubmission();
    if (submission.choice && submission.choice !== choice && (submission.pending || submission.completed)) {
      throw new Error('The artwork choice changed during submission. Please finish this add before starting a new design.');
    }
    submission.choice = choice;
    const metadata = artworkProperties({ groupId: submission.groupId, choice, role: 'item' });
    if (choice === 'create_fix') {
      if (config.currency !== 'USD') throw priceError('Paid artwork is currently supported only in USD. Free artwork review is still available.');
      if (submission.quoteCaptured ? !validQuote(submission.quote) : !displayedQuote) { refreshPrice(); throw priceError('The artwork service price is not ready. Review the displayed price before adding this design.'); }
      if (!submission.quoteCaptured) submission.quote = { ...displayedQuote };
      try { rememberAcceptedQuotes([submission.groupId], submission.quote); }
      catch (error) { error.code = 'ARTWORK_PRICE_NOT_ACCEPTED'; throw error; }
      metadata[QUOTE_KEYS[0]] = String(submission.quote.priceCents);
      metadata[QUOTE_KEYS[1]] = submission.quote.currency;
    } else priceMessage = null;
    return metadata;
  }
  function isEligibleVariant(value) {
    const product = productsByVariant.get(String(value));
    if (product) return currentProductEnabled && product.productId === config.currentProductId && product.productId !== serviceProductId;
    if (submission?.fromCurrentModal && !submission.started && currentProductEnabled) {
      throw new Error('This product variant is not included in the verified artwork setup. Please reload its product page before adding it.');
    }
    return false;
  }
  function beginMutation() {
    externalPending += 1;
    epoch += 1;
    ready = false;
    updateCheckout();
    return { done: false, submission: null, awaitReconciliation: relevant };
  }
  function registerGroups(ticket, transformed) {
    if (!transformed.touched) return;
    transportBlocker = null;
    ticket.submission = submission;
    ticket.awaitReconciliation = true;
    ticket.submission.pending += 1;
    ticket.submission.started = true;
    announce('Checking the artwork service for this design…');
  }
  function finishMutation(ticket) {
    if (ticket.done) return;
    ticket.done = true;
    if (ticket.submission) ticket.submission.pending -= 1;
    externalPending -= 1;
    if (!externalPending) { const waiters = idleWaiters; idleWaiters = []; waiters.forEach((resolve) => resolve()); }
    requestReconcile();
  }
  function waitForMutations() {
    return externalPending ? new Promise((resolve) => idleWaiters.push(resolve)) : Promise.resolve();
  }
  const transform = (body, contentType) => transformCartAddBody(body, { contentType, metadataForItem: getMetadata, isEligibleVariant,
    FormDataClass: win.FormData, URLSearchParamsClass: win.URLSearchParams });

  win.fetch = async function championArtworkFetch(input, init) {
    const method = init?.method || (RequestClass && input instanceof RequestClass ? input.method : 'GET');
    const action = routeOf(input, method);
    if (!action) return originalFetch(input, init);
    const ticket = beginMutation();
    try {
      let nextInput = input;
      let nextInit = init;
      if (action === 'add') {
        const request = RequestClass && input instanceof RequestClass ? input : null;
        const headers = new HeadersClass(init?.headers ?? request?.headers);
        const contentType = headers.get('content-type') || '';
        let body = init && own(init, 'body') ? init.body : null;
        if (body === null && request && !/^(?:GET|HEAD)$/i.test(method)) {
          const clone = request.clone();
          body = /multipart\/form-data|application\/x-www-form-urlencoded/i.test(contentType) ? await clone.formData() : await clone.text();
        }
        let transformed;
        try { transformed = transform(body, contentType); }
        catch (error) {
          if (error.code === 'ARTWORK_PRICE_NOT_ACCEPTED') { priceMessage = error.message; transportBlocker = null; }
          else transportBlocker = error.message || 'The artwork submission could not be verified.';
          block(error.message, [error.code || 'SUBMISSION_NOT_VERIFIED']);
          throw error;
        }
        if (transformed.touched) {
          // Native FormData needs a fresh multipart boundary. Do not touch other headers.
          if (transformed.body instanceof win.FormData) headers.delete('content-type');
          if (request) {
            nextInput = new RequestClass(request, { ...init, body: transformed.body, headers });
            nextInit = undefined;
          } else nextInit = { ...init, body: transformed.body, headers };
          registerGroups(ticket, transformed);
        }
      }
      if (ticket.submission?.choice === 'create_fix') await verifySubmissionPrice(ticket.submission);
      return await originalFetch(nextInput, nextInit);
    } catch (error) {
      // Do not retry the customer's add, including ambiguous network failures.
      throw error;
    } finally {
      // The theme reloads /cart as soon as its fetch resolves. Release this
      // request's ticket BEFORE awaiting the queue, or the queue deadlocks.
      finishMutation(ticket);
      if (ticket.awaitReconciliation) await settleExistingWork();
    }
  };

  if (XHR && originalOpen && originalSend && originalSetHeader) {
    XHR.prototype.open = function (method, url, ...rest) {
      const previous = xhrState.get(this);
      if (previous?.deferred) { previous.cancelled = true; if (previous.ticket) finishMutation(previous.ticket); }
      const result = originalOpen.call(this, method, url, ...rest);
      xhrState.set(this, { action: routeOf(url, method), contentType: '' });
      return result;
    };
    if (originalAbort) XHR.prototype.abort = function () {
      const state = xhrState.get(this);
      if (state?.deferred) {
        state.cancelled = true; state.deferred = false;
        if (state.ticket) finishMutation(state.ticket);
        const result = originalAbort.call(this);
        this.dispatchEvent(new win.Event('abort')); this.dispatchEvent(new win.Event('loadend'));
        return result;
      }
      return originalAbort.call(this);
    };
    XHR.prototype.setRequestHeader = function (name, value) {
      const state = xhrState.get(this);
      const result = originalSetHeader.call(this, name, value);
      if (state && String(name).toLowerCase() === 'content-type') state.contentType = String(value);
      return result;
    };
    XHR.prototype.send = function (body) {
      const state = xhrState.get(this);
      if (!state?.action) return originalSend.call(this, body);
      if (state.deferred) { const error = new Error('This XMLHttpRequest is already awaiting its artwork price check.'); error.name = 'InvalidStateError'; throw error; }
      let transformed = { body, touched: false, groups: [] };
      try { if (state.action === 'add') transformed = transform(body, state.contentType); }
      catch (error) {
        if (error.code === 'ARTWORK_PRICE_NOT_ACCEPTED') { priceMessage = error.message; transportBlocker = null; }
        else transportBlocker = error.message;
        block(error.message, [error.code || 'SUBMISSION_NOT_VERIFIED']); throw error;
      }
      // XHR headers cannot be removed after setRequestHeader. An explicitly set
      // multipart boundary would become invalid on reserialization; never send it.
      if (transformed.touched && transformed.body instanceof win.FormData && /multipart\/form-data/i.test(state.contentType)) {
        transportBlocker = 'The upload used a fixed multipart boundary. Please retry through the standard product form.';
        block(transportBlocker, ['UNSUPPORTED_MULTIPART_HEADER']);
        throw new Error(transportBlocker);
      }
      const ticket = beginMutation();
      registerGroups(ticket, transformed);
      const finish = () => finishMutation(ticket);
      this.addEventListener('loadend', finish, { once: true });
      if (ticket.submission?.choice === 'create_fix') {
        state.deferred = true; state.ticket = ticket; state.cancelled = false;
        verifySubmissionPrice(ticket.submission).then(() => {
          if (state.cancelled) return;
          state.deferred = false;
          try { originalSend.call(this, transformed.body); }
          catch (error) { finish(); block(error.message, ['SUBMISSION_NOT_VERIFIED']); this.dispatchEvent(new win.Event('error')); this.dispatchEvent(new win.Event('loadend')); }
        }, (error) => {
          if (state.cancelled) return;
          state.deferred = false; finish();
          block(error.message, ['ARTWORK_PRICE_NOT_ACCEPTED']);
          this.dispatchEvent(new win.Event('error')); this.dispatchEvent(new win.Event('loadend'));
        });
        return undefined;
      }
      try { return originalSend.call(this, transformed.body); }
      catch (error) { this.removeEventListener('loadend', finish); finish(); throw error; }
    };
  }

  async function readCart() {
    const response = await originalFetch(cartURL('.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('The cart could not be checked. Refresh the cart and try again.');
    const cart = await response.json();
    if (!object(cart) || cart.currency !== config.currency || !Array.isArray(cart.items)) {
      throw new Error('A complete cart in the selected currency is required to verify artwork charges.');
    }
    lastCart = cart;
    relevant = Boolean(submission?.pending) || cart.items.some((item) =>
      serviceIds.has(String(item.variant_id)) ||
      KEYS.some((key) => own(item.properties || {}, key)));
    if (submission && cart.items.some((item) => productsByVariant.get(String(item.variant_id))?.productId === String(item.product_id) && item.properties?._ct_artwork_group === submission.groupId)) {
      submission.completed = true;
    }
    return cart;
  }
  function validQuote(quote) { return object(quote) && Number.isSafeInteger(quote.priceCents) && quote.priceCents >= 0 && quote.currency === 'USD'; }
  const sameQuote = (a, b) => validQuote(a) && validQuote(b) && a.priceCents === b.priceCents && a.currency === b.currency;
  const formatQuote = (quote) => new Intl.NumberFormat(undefined, { style: 'currency', currency: quote.currency }).format(quote.priceCents / 100);
  async function verifyService(variantId = currentServiceId) {
    if (config.currency !== 'USD') throw new Error('Paid artwork is currently supported only in USD. Free artwork review is still available.');
    const url = new URL(`products/${config.serviceProductHandle}.js`, root);
    const response = await originalFetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('The artwork service price could not be verified.');
    const product = await response.json();
    const variant = product?.variants?.find((item) => String(item.id) === variantId);
    if (String(product?.id) !== SCOPE.serviceProductId || !variant || variant.available !== true ||
      !Number.isSafeInteger(variant.price) || variant.price < 0 || variant.requires_shipping !== false) {
      throw new Error('The artwork service identity, current price, availability or shipping status could not be verified.');
    }
    return { priceCents: variant.price, currency: config.currency };
  }
  async function verifyCartPrices(cart, groups) {
    const needed = new Set([...groups].map(([group, items]) => feeIdForGroup(group, items)));
    for (const item of cart.items) if (serviceIds.has(String(item.variant_id))) needed.add(String(item.variant_id));
    const quotes = {};
    for (const feeId of needed) quotes[feeId] = await verifyService(feeId);
    return quotes;
  }
  const priceCentsMap = (quotes) => Object.fromEntries(Object.entries(quotes).map(([id, quote]) => [id, quote.priceCents]));
  function updatePriceNote() {
    const note = doc.getElementById('champion-artwork-service-price-note');
    if (!note) return;
    note.textContent = config.currency !== 'USD' ? 'Upload artwork for review: free. Paid design help is currently supported only in USD.' : displayedQuote
      ? `Upload artwork for review: free. Help me create my design: ${formatQuote(displayedQuote)} per design, regardless of product quantity.`
      : 'Upload artwork for review: free. The artwork service price is being checked or is unavailable. Please wait for its price before choosing paid design help.';
  }
  function refreshPrice() {
    if (displayRequest) return displayRequest;
    displayedQuote = null; updatePriceNote();
    displayRequest = verifyService().then((quote) => { displayedQuote = quote; updatePriceNote(); return quote; }, (error) => {
      displayedQuote = null; updatePriceNote(); return null;
    }).finally(() => { displayRequest = null; });
    return displayRequest;
  }
  function rememberAcceptedQuotes(groups, quote) {
    if (quoteStoreError) throw new Error(quoteStoreError);
    const next = { ...acceptedQuotes };
    for (const group of groups) next[group] = { priceCents: quote.priceCents, currency: quote.currency };
    try { win.sessionStorage.setItem(quoteKey, JSON.stringify(next)); acceptedQuotes = next; }
    catch { throw new Error('This browser could not safely remember the accepted artwork price.'); }
  }
  async function verifySubmissionPrice(active) {
    let fresh;
    try { fresh = await verifyService(); }
    catch (error) { displayedQuote = null; updatePriceNote(); priceMessage = error.message; throw error; }
    displayedQuote = fresh; updatePriceNote();
    if (!sameQuote(active.quote, fresh)) {
      priceMessage = `The artwork service price is now ${formatQuote(fresh)} per design. Review it and click Add again to accept the updated price.`;
      announce(priceMessage, true);
      throw new Error(priceMessage);
    }
    priceMessage = null;
  }
  function paidGroups(cart) {
    const groups = new Map();
    for (const item of cart.items) {
      const known = productsByVariant.get(String(item.variant_id));
      if (!known?.eligible || known.productId !== String(item.product_id) || known.productId === serviceProductId || item.properties?._ct_artwork_choice !== 'create_fix') continue;
      const group = item.properties?._ct_artwork_group;
      if (typeof group !== 'string') continue;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(item);
    }
    return groups;
  }
  function acceptedGroupQuote(group, items) {
    if (validQuote(acceptedQuotes[group])) return acceptedQuotes[group];
    const quotes = items.map((item) => ({ priceCents: /^\d+$/.test(item.properties?.[QUOTE_KEYS[0]] || '') ? Number(item.properties[QUOTE_KEYS[0]]) : NaN, currency: item.properties?.[QUOTE_KEYS[1]] }));
    return quotes.length && quotes.every((quote) => sameQuote(quote, quotes[0])) ? quotes[0] : null;
  }
  function offerPriceApproval(groups, quote, feeId = currentServiceId) {
    if (priceApprovalPending) return block('Checking the current artwork price. Please wait…', ['ARTWORK_PRICE_CHECK_PENDING']);
    block(`Artwork help is currently ${formatQuote(quote)} per design. Please accept this price before the service is added or checkout continues.`, ['ARTWORK_PRICE_NOT_ACCEPTED']);
    if (!notice) return false;
    const button = doc.createElement('button');
    button.id = 'champion-artwork-price-accept'; button.type = 'button';
    button.textContent = `Accept ${formatQuote(quote)} per design`;
    button.style.cssText = 'display:block;margin-top:8px;padding:8px 12px;cursor:pointer';
    button.addEventListener('click', async () => {
      if (button.disabled || priceApprovalPending) return;
      priceApprovalPending = true;
      button.disabled = true;
      button.textContent = 'Checking current price…';
      announce('Checking the current artwork price. Please wait…');
      try {
        const fresh = await verifyService(feeId);
        if (feeId === currentServiceId) { displayedQuote = fresh; updatePriceNote(); }
        if (!sameQuote(fresh, quote)) { priceApprovalPending = false; offerPriceApproval(groups, fresh, feeId); return; }
        rememberAcceptedQuotes(groups, fresh);
        priceApprovalPending = false;
        priceMessage = null;
        await requestReconcile();
      } catch (error) { priceApprovalPending = false; block(error.message, ['ARTWORK_PRICE_NOT_ACCEPTED']); }
      finally { priceApprovalPending = false; button.disabled = false; }
    });
    notice.appendChild(button);
    return false;
  }
  const cartIdentity = (cart) => typeof cart.token === 'string' ? cart.token.split('?')[0] : null;
  const sameCart = (record, cart) => record.cartId === null || cartIdentity(cart) === null || record.cartId === cartIdentity(cart);
  function writePendingFeeAdds(next) {
    if (journalError) throw new Error(journalError);
    try {
      const serialized = JSON.stringify(next);
      win.sessionStorage.setItem(journalKey, serialized);
      for (const key of legacyJournalKeys) win.sessionStorage.setItem(key, serialized);
      pendingFeeAdds = next;
    }
    catch { journalError = 'This browser could not safely remember the pending artwork charge.'; throw new Error(journalError); }
  }
  function clearConfirmedFeeAdds(cart, staleFeeKeys = []) {
    const staleKeys = new Set(staleFeeKeys);
    const remaining = pendingFeeAdds.filter((record) => !sameCart(record, cart) ||
      !cart.items.some((item) => !staleKeys.has(item.key) && serviceIds.has(String(item.variant_id)) &&
        item.properties?._ct_artwork_role === 'fee' && item.properties?._ct_artwork_group === record.groupId));
    if (remaining.length !== pendingFeeAdds.length) writePendingFeeAdds(remaining);
  }
  function refreshed(cart) {
    const eventTarget = doc.documentElement || doc;
    eventTarget.dispatchEvent(new win.CustomEvent('champion:artwork-cart-updated', { bubbles: true, detail: { cart, repaired: true } }));
    eventTarget.dispatchEvent(new win.CustomEvent('cart:refresh', { bubbles: true, detail: { cart, source: 'champion-artwork-service' } }));
    scheduleCartPageReload();
  }
  function verifyKnownCartEligibility(cart) {
    const unknown = cart.items.filter((item) => !serviceIds.has(String(item.variant_id)) &&
      KEYS.some((key) => own(item.properties || {}, key)) &&
      productsByVariant.get(String(item.variant_id))?.productId !== String(item.product_id));
    if (!unknown.length) return true;
    const stamp = JSON.stringify([cartIdentity(cart), [...new Set(unknown.map((item) => `${item.product_id}:${item.variant_id}`))].sort()]);
    let alreadyRefreshed;
    try { alreadyRefreshed = win.sessionStorage.getItem(eligibilityReloadKey) === stamp; }
    catch { return block('Artwork product eligibility could not be verified. Please refresh the cart.', ['ELIGIBILITY_NOT_VERIFIED']); }
    if (alreadyRefreshed || eligibilityReloadScheduled || typeof win.setTimeout !== 'function') {
      return block('An artwork product is missing from the verified cart setup. Please contact us before checkout.', ['ELIGIBILITY_NOT_VERIFIED']);
    }
    try { win.sessionStorage.setItem(eligibilityReloadKey, stamp); }
    catch { return block('Artwork product eligibility could not be verified. Please refresh the cart.', ['ELIGIBILITY_NOT_VERIFIED']); }
    eligibilityReloadScheduled = true;
    block('Refreshing the cart to verify its artwork products.', ['ELIGIBILITY_REFRESH_REQUIRED']);
    win.setTimeout(async () => {
      await settleExistingWork();
      if (!externalPending && !reloadIssued) { reloadIssued = true; win.location.reload(); }
    }, 0);
    return false;
  }
  function scheduleCartPageReload() {
    const isCartPage = win.theme?.pageType === 'cart' ||
      new URL(win.location.href).pathname.replace(/\/$/, '') === new URL(cartURL('')).pathname.replace(/\/$/, '');
    if (!isCartPage || !ready || reloadScheduled || reloadIssued || typeof win.setTimeout !== 'function') return;
    reloadScheduled = true;
    // Fetch callers usually reload themselves. This fallback covers XHR and
    // page-load repairs; wait briefly to let the theme's own navigation win.
    win.setTimeout(async () => {
      try {
        await settleExistingWork();
        if (!ready || externalPending || !lastCart || reloadIssued) return;
        const stamp = JSON.stringify([cartIdentity(lastCart), lastCart.items.map((item) =>
          [item.key, item.quantity, item.final_line_price ?? item.line_price ?? null])]);
        // Persist before navigation: repeated identical boot repairs cannot loop.
        try {
          if (win.sessionStorage.getItem(reloadKey) === stamp) return;
          win.sessionStorage.setItem(reloadKey, stamp);
        } catch {
          announce('The cart was updated. Please refresh this page to see its current total.');
          return;
        }
        reloadIssued = true;
        win.location.reload();
      } finally { reloadScheduled = false; }
    }, 250);
  }
  async function reconcilePass() {
    const attempted = new Set();
    let repaired = false;
    for (let count = 0; count < 50; count += 1) {
      await waitForMutations();
      const snapshotEpoch = epoch;
      const cart = await readCart();
      if (externalPending || epoch !== snapshotEpoch) continue;
      if (transportBlocker) return block(transportBlocker, ['SUBMISSION_NOT_VERIFIED']);
      if (!verifyKnownCartEligibility(cart)) return false;
      const groups = paidGroups(cart);
      if (groups.size && repriceJournalError) return block(repriceJournalError, ['PENDING_REPRICE_TRANSACTION']);
      if (pendingReprices.some((record) => sameCart(record, cart) && groups.has(record.groupId))) {
        return block('An earlier artwork price update is still unconfirmed. The design must be removed from the cart before it can be added again safely.', ['PENDING_REPRICE_TRANSACTION']);
      }
      const freshPrice = await verifyCartPrices(cart, groups);
      if (externalPending || epoch !== snapshotEpoch) continue;
      const plan = planVariantArtworkReconciliation(cart, { ...plannerConfig, servicePrices: priceCentsMap(freshPrice) });
      if (plan.blockers.length) {
        const codes = plan.blockers.map((item) => item.code);
        const price = codes.some((code) => /PRICE|DISCOUNT|CURRENCY/.test(code));
        return block(price ? 'The current artwork price or discount exclusion could not be verified.' : 'The cart contains artwork details that need review.', codes);
      }
      clearConfirmedFeeAdds(cart, plan.staleFeeKeys);
      if (groups.size && quoteStoreError) return block(quoteStoreError, ['ARTWORK_PRICE_NOT_ACCEPTED']);
      const unaccepted = [...groups].filter(([group, items]) => !sameQuote(acceptedGroupQuote(group, items), quoteForGroup(group, items, freshPrice))).map(([group]) => group);
      if (unaccepted.length) {
        const firstGroup = unaccepted[0];
        const feeId = feeIdForGroup(firstGroup, groups.get(firstGroup));
        return offerPriceApproval(unaccepted.filter((group) => feeIdForGroup(group, groups.get(group)) === feeId), freshPrice[feeId], feeId);
      }
      if (plan.readyForCheckout) {
        unconfirmedMutation = false;
        ready = true;
        if (firstVerifiedPass) {
          firstVerifiedPass = false;
          // A clean newly-rendered page establishes a fresh display baseline.
          if (!repaired) try { win.sessionStorage.removeItem(reloadKey); } catch { /* Optional UI refresh bookkeeping only. */ }
        }
        announce(priceMessage || '');
        updateCheckout();
        if (repaired) {
          refreshed(cart);
          if (plan.summary.paidDesignCount > 0) announce('Artwork charges match the selected service for each design. To remove a charge, remove the associated product or change its artwork option.');
        }
        return true;
      }
      if (plan.updates.some((update) => plan.staleFeeKeys?.includes(update.key) && pendingFeeAdds.some((record) =>
        sameCart(record, cart) && record.groupId === update.groupId))) {
        return block('An earlier artwork service request is still unconfirmed. The existing fee was left unchanged. Refresh to check its status, or remove the associated product.', ['PENDING_FEE_TRANSACTION']);
      }
      if (plan.adds.some((add) => pendingFeeAdds.some((record) =>
        sameCart(record, cart) && record.groupId === add.properties._ct_artwork_group))) {
        return block('An earlier artwork service request is still unconfirmed. Refresh to check its status, or remove the associated product.', ['PENDING_FEE_TRANSACTION']);
      }
      if (unconfirmedMutation) return block('An earlier artwork cart change is still unconfirmed. Refresh the cart to check it again.', ['MUTATION_NOT_CONFIRMED']);
      ready = false;
      announce('Updating the artwork service charge…');
      updateCheckout();
      const update = plan.updates[0];
      const add = update ? null : plan.adds[0];
      const reprice = update && plan.staleFeeKeys?.includes(update.key);
      const signature = update ? `change:${update.key}:${update.quantity}` : `add:${add.properties._ct_artwork_group}`;
      if (attempted.has(signature)) {
        unconfirmedMutation = true;
        return block('An artwork cart change was not confirmed. Refresh the cart to check it again.', ['MUTATION_NOT_CONFIRMED']);
      }
      if (add || reprice) {
        const feeId = add ? String(add.id) : feeIdForGroup(update.groupId, groups.get(update.groupId));
        const beforeAdd = await verifyService(feeId);
        if (!sameQuote(beforeAdd, freshPrice[feeId])) {
          if (feeId === currentServiceId) { displayedQuote = beforeAdd; updatePriceNote(); }
          return offerPriceApproval([...groups].filter(([group, items]) => feeIdForGroup(group, items) === feeId).map(([group]) => group), beforeAdd, feeId);
        }
      }
      if (externalPending || epoch !== snapshotEpoch) continue;
      attempted.add(signature);
      // Save BEFORE sending. A reload/timeout cannot silently repeat this add.
      if (add) writePendingFeeAdds([...pendingFeeAdds, { cartId: cartIdentity(cart), groupId: add.properties._ct_artwork_group }]);
      // Persist before removing a stale line. A delayed quantity=0 request must
      // never race a replacement with the same Shopify line key after reload.
      const repriceRecord = reprice ? { cartId: cartIdentity(cart), groupId: update.groupId, key: update.key } : null;
      if (repriceRecord) writePendingReprices([...pendingReprices, repriceRecord]);
      let mutationError = false;
      try {
        const response = await originalFetch(cartURL(update ? '/change.js' : '/add.js'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(update ? { id: update.key, quantity: update.quantity } : { items: [add] }),
        });
        mutationError = !response.ok;
        // Only a completed success response settles this removal. A later cart
        // snapshot cannot prove that an earlier timed-out removal has finished.
        if (repriceRecord && response.ok) writePendingReprices(pendingReprices.filter((record) => record !== repriceRecord));
      } catch { mutationError = true; }
      repaired = true;
      // Even on timeout/error, read first. Never blindly retry an uncertain add.
      if (mutationError) {
        if (repriceRecord) return block('The artwork price update could not be confirmed. Remove the associated design before adding it again; this update will not be retried automatically.', ['PENDING_REPRICE_TRANSACTION']);
        await waitForMutations();
        const verificationEpoch = epoch;
        const verified = await readCart();
        const verifiedGroups = paidGroups(verified);
        const verifyPrice = await verifyCartPrices(verified, verifiedGroups);
        const verifyPlan = planVariantArtworkReconciliation(verified, { ...plannerConfig, servicePrices: priceCentsMap(verifyPrice) });
        const unacceptedVerified = [...paidGroups(verified)].filter(([group, items]) => !sameQuote(acceptedGroupQuote(group, items), quoteForGroup(group, items, verifyPrice))).map(([group]) => group);
        if (unacceptedVerified.length) {
          const feeId = feeIdForGroup(unacceptedVerified[0], verifiedGroups.get(unacceptedVerified[0]));
          return offerPriceApproval(unacceptedVerified.filter((group) => feeIdForGroup(group, verifiedGroups.get(group)) === feeId), verifyPrice[feeId], feeId);
        }
        if (verifyPlan.readyForCheckout && !externalPending && epoch === verificationEpoch) {
          clearConfirmedFeeAdds(verified, verifyPlan.staleFeeKeys);
          firstVerifiedPass = false;
          ready = true; announce(priceMessage || ''); updateCheckout(); refreshed(verified); return true;
        }
        // A changed cart can still need other repairs after this one succeeded.
        const applied = !verifyPlan.blockers.length && (add
          ? verified.items.some((item) => serviceIds.has(String(item.variant_id)) && item.properties?._ct_artwork_group === add.properties._ct_artwork_group)
          : update.quantity === 0
            ? !verified.items.some((item) => item.key === update.key)
            : verified.items.some((item) => item.key === update.key && item.quantity === update.quantity));
        if (applied && !externalPending && epoch === verificationEpoch) continue;
        unconfirmedMutation = true;
        refreshed(verified);
        return block('An artwork cart change could not be confirmed. Refresh the cart to check it again.', ['MUTATION_NOT_CONFIRMED']);
      }
      // Exactly one write per fresh cart. Line keys may have changed.
    }
    if (repaired && lastCart) refreshed(lastCart);
    return block('The cart kept changing while artwork was checked. Please retry from the cart.', ['CART_DID_NOT_SETTLE']);
  }
  function requestReconcile() {
    dirty = true;
    ready = false;
    updateCheckout();
    if (!running) {
      running = Promise.resolve().then(async () => {
        while (dirty) {
          dirty = false;
          try {
            if (win.navigator?.locks?.request) await win.navigator.locks.request('champion-artwork-service-cart', reconcilePass);
            else await reconcilePass();
          } catch (error) { block(error.message || 'Artwork charges could not be verified.', ['CART_VERIFICATION_FAILED']); }
        }
        return ready;
      }).finally(() => { running = null; if (dirty) requestReconcile(); });
    }
    return running;
  }
  async function settleExistingWork() {
    while (externalPending || running) {
      await waitForMutations();
      if (running) await running;
    }
    return ready || !relevant;
  }
  async function whenIdle() {
    await requestReconcile();
    return settleExistingWork();
  }
  function showPriceExplanation() {
    if (!currentProductEnabled) return;
    const select = doc.querySelector(SELECTOR);
    if (!select || doc.getElementById('champion-artwork-service-price-note')) return;
    const note = doc.createElement('p');
    note.id = 'champion-artwork-service-price-note';
    note.style.cssText = 'margin:8px 0;font-size:14px;line-height:1.4';
    select.insertAdjacentElement('afterend', note);
    const described = select.getAttribute('aria-describedby');
    select.setAttribute('aria-describedby', [described, note.id].filter(Boolean).join(' '));
    updatePriceNote();
    refreshPrice();
  }
  doc.addEventListener('click', (event) => {
    if (currentProductEnabled && choiceFromLabel(doc.querySelector(SELECTOR)?.value) && event.target?.closest?.(ADD_SELECTOR)) {
      if (submission && (submission.pending > 0 || (submission.started && running))) {
        event.preventDefault(); event.stopImmediatePropagation();
        announce('Please wait for this artwork submission to finish before adding another design.');
        return;
      }
      if (!submission || (submission.completed && submission.pending === 0 && externalPending === 0 && !running)) {
        try { submission = { ...newSubmission(), fromCurrentModal: true }; }
        catch (error) { event.preventDefault(); event.stopImmediatePropagation(); block(error.message, ['NO_SUBMISSION_ID']); }
      }
      if (submission) { submission.quote = displayedQuote ? { ...displayedQuote } : null; submission.quoteCaptured = true; }
    }
    const checkout = event.composedPath?.().find((node) => node?.matches?.(checkoutSelector)) || event.target?.closest?.(checkoutSelector);
    if (checkout && relevant && !ready) {
      event.preventDefault(); event.stopImmediatePropagation();
      announce('Please wait while artwork charges are checked. If this message stays, refresh the cart.', true);
      requestReconcile();
    }
  }, true);
  doc.addEventListener('submit', (event) => {
    const form = event.target;
    let action;
    try { action = new URL(form.action || '', win.location.href).pathname; } catch { return; }
    const checkout = /\/(?:checkouts?|checkout\/[^/]*)\/?$/.test(action) || event.submitter?.name === 'checkout' || form.querySelector?.('input[type="hidden"][name="checkout"]');
    if (checkout && relevant && !ready) {
      event.preventDefault(); event.stopImmediatePropagation();
      announce('Please wait while artwork charges are checked. If this message stays, refresh the cart.', true);
      requestReconcile();
    }
  }, true);
  if (win.MutationObserver && doc.documentElement) {
    mutationObserver = new win.MutationObserver(() => { showPriceExplanation(); updateCheckout(); });
    mutationObserver.observe(doc.documentElement, { subtree: true, childList: true });
  }
  win.addEventListener('pageshow', requestReconcile);
  doc.addEventListener('visibilitychange', () => { if (doc.visibilityState === 'visible') { if (currentProductEnabled) refreshPrice(); requestReconcile(); } });
  doc.addEventListener('change', (event) => { if (event.target === doc.querySelector(SELECTOR) && currentProductEnabled) refreshPrice(); });
  const api = Object.freeze({ disabled: false, whenIdle, reconcile: whenIdle, refreshPrice,
    get status() { return { readyForCheckout: ready || !relevant, relevantCart: relevant, pendingRequests: externalPending, verifying: Boolean(running), blocked: relevant && !ready }; } });
  win.ChampionArtworkService = api;
  showPriceExplanation();
  requestReconcile();
  return api;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  let config;
  try { config = JSON.parse(document.getElementById('champion-artwork-service-config')?.textContent || 'null'); }
  catch { config = null; }
  installArtworkService(window, document, config);
}

